import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useUpdateTariffFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.form-validation';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { TariffDimensionType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useTariffUIContext } from '@app/feature/tariff/tariff.context';

import * as css from '../tariff-update-form-step-3/tariff-update-form-step-3.module.scss';

interface PriceSelectionProps {
  elementIndex: number;
  priceComponentIndex: number;
}

const EnergySelection = ({
  elementIndex,
  priceComponentIndex,
}: PriceSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<UpdateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useUpdateTariffFormValidation();

  useEffect(() => {
    setValue(
      `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.ENERGY,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
          control={control}
          rules={priceComponentPrice(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-price-component.price')}
                placeholder="0"
                required
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>{getValues('tariff.currency')}</Text>
      </Stack>

      <Text color={TextColor.Secondary}>{t('$.tariff-update.step-3.by')}</Text>

      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
          control={control}
          rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-price-component.step-size',
                )}
                placeholder="0"
                required
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>kWh</Text>
      </Stack>
    </Stack>
  );
};

const TimeSelection = ({
  elementIndex,
  priceComponentIndex,
}: PriceSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<UpdateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useUpdateTariffFormValidation();

  useEffect(() => {
    setValue(
      `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.TIME,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
          control={control}
          rules={priceComponentPrice(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-price-component.price')}
                placeholder="0"
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>{getValues('tariff.currency')}</Text>
      </Stack>

      <Text color={TextColor.Secondary}>{t('$.tariff-update.step-3.by')}</Text>

      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
          control={control}
          rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-price-component.step-size',
                )}
                placeholder="0"
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />

        <Text color={TextColor.Secondary}>min</Text>
      </Stack>
    </Stack>
  );
};

interface ParkingSelectionProps extends PriceSelectionProps {
  hasParkingPrice: boolean;
  setHasParkingPrice: (value: boolean) => void;
}

const ParkingSelection = ({
  elementIndex,
  priceComponentIndex,
  hasParkingPrice,
  setHasParkingPrice,
}: ParkingSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<UpdateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useUpdateTariffFormValidation();

  useEffect(() => {
    setValue(
      `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.PARKING_TIME,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} direction="column">
      <Checkbox
        id="different-tariff-after-charge"
        label={t('$.tariff-update.step-3.parking.different-tariff.label')}
        checked={hasParkingPrice}
        onChange={(e) => setHasParkingPrice(e.target.checked)}
      />
      {hasParkingPrice && (
        <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
          <Stack gap={GapEnum.S} alignItems="center">
            <Controller
              name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
              control={control}
              rules={priceComponentPrice(elementIndex, priceComponentIndex)}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    label={t(
                      'delmonicos-shared-sdk.outputs-price-component.price',
                    )}
                    placeholder="0"
                    errorMessage={error?.message}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.valueAsNumber,
                      )
                    }
                  />
                );
              }}
            />
            <Text color={TextColor.Secondary}>
              {getValues('tariff.currency')}
            </Text>
          </Stack>

          <Text color={TextColor.Secondary}>
            {t('$.tariff-update.step-3.by')}
          </Text>

          <Stack gap={GapEnum.S} alignItems="center">
            <Controller
              name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
              control={control}
              rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    label={t(
                      'delmonicos-shared-sdk.outputs-price-component.step-size',
                    )}
                    placeholder="0"
                    errorMessage={error?.message}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.valueAsNumber,
                      )
                    }
                  />
                );
              }}
            />
            <Text color={TextColor.Secondary}>min</Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const TariffTimeSlot = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { firstRange, setFirstRange } = useTariffUIContext();

  const setHasParkingPrice = (value: boolean) => {
    const newFirstRange = { ...firstRange };
    newFirstRange.firstTimeSlot.hasParkingPrice = value;
    setFirstRange(newFirstRange);
  };

  return (
    <Stack gap={GapEnum.S} flex={1} direction="column">
      <Card flex={1} gap={GapEnum.M}>
        <AccentTitle
          icon="lightning-01"
          text={t('$.tariff-update.step-2-alternate.energy')}
        />
        <EnergySelection elementIndex={0} priceComponentIndex={0} />
      </Card>

      <div style={{ alignSelf: 'center' }}>
        <Text>+</Text>
      </div>

      <Card
        flex={1}
        gap={GapEnum.M}
        direction="column"
        className={css.parkingSelectionCard}>
        <AccentTitle
          icon="clock-fast-forward"
          text={t('$.tariff-update.step-2-alternate.time')}
          variant={AccentVariantEnum.Secondary}
        />
        <TimeSelection elementIndex={0} priceComponentIndex={1} />
        <Divider />
        <ParkingSelection
          elementIndex={0}
          priceComponentIndex={2}
          hasParkingPrice={firstRange.firstTimeSlot.hasParkingPrice}
          setHasParkingPrice={setHasParkingPrice}
        />
      </Card>
    </Stack>
  );
};

interface PreviewCardTimeSlotProps {
  currency: string;
}

const PreviewCardTimeSlot = ({
  currency,
}: PreviewCardTimeSlotProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { flatValue, firstRange } = useTariffUIContext();
  const { watch } = useFormContext<UpdateTariffModel>();
  const energy = watch('tariff.elements.0.priceComponents.0');
  const time = watch('tariff.elements.0.priceComponents.1');
  const parking = watch('tariff.elements.0.priceComponents.2');

  return (
    <Card flex={1} gap={GapEnum.M} className={css.timeSlotPreview}>
      <Stack gap={GapEnum.S} alignItems="center">
        <Icon name="price" />
        <Text bold>
          {t('$.tariff-update.step-2-alternate.preview.time-slot.tariff')}
        </Text>
      </Stack>

      <Stack gap={GapEnum.S} flex={1}>
        <Card
          flex={1}
          gap={GapEnum.M}
          alignItems="center"
          justifyContent="center">
          <Text bold color={TextColor.Secondary}>
            {energy.price ?? '-'} {currency} / {energy.stepSize ?? '-'} kWh
          </Text>
        </Card>

        <Stack alignItems="center">
          <Text bold>+</Text>
        </Stack>

        <Card
          flex={1}
          gap={GapEnum.M}
          alignItems="center"
          justifyContent="center">
          <Stack flex={1} alignItems="center" justifyContent="center">
            <Text bold color={TextColor.Secondary}>
              {time?.price ?? '-'} {currency} / {time?.stepSize ?? '-'} min
            </Text>
          </Stack>

          {firstRange.firstTimeSlot.hasParkingPrice && (
            <Stack
              flex={1}
              alignItems="center"
              justifyContent="center"
              className={css.parkingPreview}>
              <Text color={TextColor.Secondary}>
                {parking?.price ?? '-'} {currency} / {parking?.stepSize ?? '-'}{' '}
                min
              </Text>
            </Stack>
          )}
        </Card>
      </Stack>

      {flatValue && (
        <Stack justifyContent="space-between" alignItems="center">
          <Stack gap={GapEnum.S} alignItems="center">
            <Icon name="home-03" />
            <Text>{t('$.tariff-update.step-3.preview.flat-value')}</Text>
          </Stack>
          <Text bold>
            {flatValue} {currency}
          </Text>
        </Stack>
      )}
    </Card>
  );
};

const PreviewCardRangeFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<UpdateTariffModel>();

  return (
    <Card gap={GapEnum.M} className={css.previewCard}>
      <AccentTitle
        icon="eye"
        text={t('$.tariff-update.step-3.preview.title')}
        variant={AccentVariantEnum.Unavailable}
      />

      <PreviewCardTimeSlot currency={getValues('tariff.currency')!} />
    </Card>
  );
};

export const TariffUpdateFormStep2AlternateComponent =
  (): React.JSX.Element => {
    return (
      <Stack gap={GapEnum.M} direction="column" flex={1}>
        <Stack gap={GapEnum.M} flex={1}>
          <TariffTimeSlot />

          <PreviewCardRangeFragment />
        </Stack>
      </Stack>
    );
  };
